import { MAX_ANCHOR } from "../components/shared/constants";

export function isResolution(resolution) {
  return resolution && resolution.width && resolution.height;
}

export function polygonToRepresentation(polygon) {
  return polygon.map((point) => point.join(",")).join(";");
}

export function textPolygonToInternalValue(polygon) {
  const points = polygon.split(";");
  return points.map((point) => {
    const [x, y] = point.split(",");
    return [parseInt(x), parseInt(y)];
  });
}

export function getTextPolygonAnchor(textPolygon, resolution) {
  if (!isResolution(resolution)) {
    // eslint-disable-next-line no-param-reassign
    resolution = { width: 3000, height: 3000 };
  }
  const polygon = textPolygonToInternalValue(textPolygon);
  // Anchor detection inspects the first/last two vertices; a degenerate polygon
  // with fewer than two points has no detectable anchor (and would otherwise
  // crash on the `second`/`beforeLast` lookups below).
  if (polygon.length < 2) return "";
  const first = polygon[0];
  const second = polygon[1];
  const last = polygon.slice(-1)[0];
  const beforeLast = polygon.slice(-2)[0];
  // [ [0,30] , [10,30] , [20,60] , [0,60] ]
  if (first[0] === 0 && first[1] === second[1] && last[0] === 0 && last[1] === beforeLast[1]) {
    return "left";
  }
  // [ [3000,30] , [10,30] , [20,60] , [3000,60] ]
  if (
    first[0] === resolution.width &&
    first[1] === second[1] &&
    last[0] === resolution.width &&
    last[1] === beforeLast[1]
  ) {
    return "right";
  }
  // [ [10,0] , [10,30] , [20,60] , [20,0] ]
  if (first[1] === 0 && first[0] === second[0] && last[1] === 0 && last[0] === beforeLast[0]) {
    return "top";
  }
  // [ [10,3000] , [10,30] , [20,60] , [20,3000] ]
  if (
    first[1] === resolution.height &&
    first[0] === second[0] &&
    last[1] === resolution.height &&
    last[0] === beforeLast[0]
  ) {
    return "bot";
  }
  return "";
}

export function anchorTextPolygon(textPolygon, anchor, resolution) {
  let maxX = MAX_ANCHOR;
  let maxY = MAX_ANCHOR;
  if (isResolution(resolution)) {
    maxX = resolution.width;
    maxY = resolution.height;
  }
  if (!anchor || !textPolygon) {
    return [];
  }
  const polygon = textPolygonToInternalValue(textPolygon);
  const [xFirst, yFirst] = polygon[0];
  const [xLast, yLast] = polygon.slice(-1)[0];
  let anchors = [];
  if (anchor === "left") {
    anchors = [`0,${yFirst}`, `0,${yLast}`];
  }
  if (anchor === "right") {
    anchors = [`${maxX},${yFirst}`, `${maxX},${yLast}`];
  }
  if (anchor === "top") {
    anchors = [`${xFirst},0`, `${xLast},0`];
  }
  if (anchor === "bot") {
    anchors = [`${xFirst},${maxY}`, `${xLast},${maxY}`];
  }
  return `${anchors[0]};${polygonToRepresentation(polygon)};${anchors[1]}`;
}

export function extractTextPolygonAnchorPoints(textPolygon) {
  const points = textPolygon.split(";");
  const firstPoint = points.shift();
  const lastPoint = points.pop();
  return [points.join(";"), firstPoint, lastPoint];
}

export function formatViewPoints(viewArea) {
  if (viewArea) return `${viewArea.length} view points`;
  return "0 points";
}

// A polygon must enclose an area, so it needs at least three vertices.
export const MIN_POLYGON_VERTICES = 3;

export function isValidPolygon(points) {
  return (
    Array.isArray(points) &&
    points.length >= MIN_POLYGON_VERTICES &&
    points.every(
      (point) => Array.isArray(point) && point.length === 2 && Number.isInteger(point[0]) && Number.isInteger(point[1])
    )
  );
}

/**
 * Infer a frame size from a polygon's own extent (its max x / y).
 *
 * A genuinely edge-anchored polygon pins its endpoints to the frame border, so
 * the largest coordinate equals the frame width/height. This lets anchor
 * detection work even when the real resolution is unavailable.
 */
export function inferResolutionFromPolygonText(textPolygon) {
  const points = textPolygonToInternalValue(textPolygon);
  if (!points.length) return null;
  let width = 0;
  let height = 0;
  points.forEach(([x, y]) => {
    if (Number.isInteger(x) && x > width) width = x;
    if (Number.isInteger(y) && y > height) height = y;
  });
  return width > 0 && height > 0 ? { width, height } : null;
}

/**
 * Infer a frame size from a whole multipolygon (the max extent across every
 * polygon). Used as a sensible default frame when the view's real resolution is
 * unavailable, so adding or re-pinning an anchor lands on-frame instead of at
 * the hardcoded 1920×1080 default.
 */
export function inferResolutionFromMultiPolygon(value) {
  if (!Array.isArray(value)) return null;
  let width = 0;
  let height = 0;
  value.forEach((polygon) => {
    const res = inferResolutionFromPolygonText(polygonToRepresentation(polygon));
    if (res) {
      if (res.width > width) width = res.width;
      if (res.height > height) height = res.height;
    }
  });
  return width > 0 && height > 0 ? { width, height } : null;
}

/**
 * Detect a polygon's edge anchor, preferring the real frame resolution and
 * falling back to the polygon's own extent when it is unknown or does not match
 * (e.g. a 704-wide DVR viewed with the 1920×1080 default).
 */
export function detectAnchor(textPolygon, resolution) {
  if (isResolution(resolution)) {
    const anchor = getTextPolygonAnchor(textPolygon, resolution);
    if (anchor) return anchor;
  }
  const inferred = inferResolutionFromPolygonText(textPolygon);
  return inferred ? getTextPolygonAnchor(textPolygon, inferred) : "";
}

/**
 * Resolve a view's `resolution` reference to a `{ width, height }` object.
 *
 * The reference may be a numeric foreign-key id, an object carrying an `id`, or
 * an object already holding `width`/`height`. Returns `null` when it cannot be
 * resolved against the supplied resolution list.
 */
export function resolveResolution(ref, resolutions) {
  if (ref === null || ref === undefined) return null;
  let id = ref;
  if (typeof ref === "object") {
    if (Number.isFinite(ref.width) && Number.isFinite(ref.height)) {
      return { width: ref.width, height: ref.height };
    }
    if (ref.id === null || ref.id === undefined) return null;
    id = ref.id;
  }
  // The serializer may send the FK as a string ("18"); compare numerically.
  const numericId = Number(id);
  const match = (resolutions || []).find((r) => Number(r.id) === numericId);
  return match ? { width: match.width, height: match.height } : null;
}

/**
 * Split a stored multipolygon into per-polygon edit rows.
 *
 * Each row carries the editable middle text (`x,y;x,y…`), the detected edge
 * anchor ("top" | "bot" | "left" | "right" | ""), and the pinned endpoint
 * coordinates (`firstPoint`/`lastPoint`) kept verbatim from the stored polygon
 * so they can be displayed without recomputing them from a frame size.
 */
export function multiPolygonToRows(value, resolution) {
  if (!Array.isArray(value)) return [];
  return value.map((polygon) => {
    const fullText = polygonToRepresentation(polygon);
    const anchor = fullText ? detectAnchor(fullText, resolution) : "";
    if (anchor) {
      const [middleText, firstPoint, lastPoint] = extractTextPolygonAnchorPoints(fullText);
      return { middleText, anchor, firstPoint, lastPoint };
    }
    return { middleText: fullText, anchor: "", firstPoint: "", lastPoint: "" };
  });
}

/**
 * Validate one edit row, returning a human-readable error or `null` when valid.
 *
 * An empty row is not an error (it is simply ignored on save). A non-empty row
 * must have well-formed `x,y` integer vertices and, once its anchor endpoints
 * are accounted for, at least {@link MIN_POLYGON_VERTICES} vertices in total.
 */
export function polygonRowError(row) {
  const middle = (row.middleText || "").trim();
  if (middle === "") return null;
  const tokens = middle
    .split(";")
    .map((token) => token.trim())
    .filter(Boolean);
  for (let i = 0; i < tokens.length; i += 1) {
    if (!/^-?\d+\s*,\s*-?\d+$/.test(tokens[i])) {
      return `Vertex ${i + 1} ("${tokens[i]}") must be "x,y" integers`;
    }
  }
  const total = tokens.length + (row.anchor ? 2 : 0);
  if (total < MIN_POLYGON_VERTICES) {
    return `A polygon needs at least ${MIN_POLYGON_VERTICES} vertices`;
  }
  return null;
}

/**
 * Apply (or clear) an edge anchor on a row, recomputing the pinned endpoints
 * for the given frame size. Clearing the anchor or an empty middle drops the
 * endpoints.
 */
export function applyAnchorToRow(row, anchor, resolution) {
  const middle = (row.middleText || "").trim();
  if (!anchor || middle === "") {
    return { ...row, anchor: anchor || "", firstPoint: "", lastPoint: "" };
  }
  const fullText = anchorTextPolygon(middle, anchor, resolution);
  if (typeof fullText !== "string") {
    return { ...row, anchor: "", firstPoint: "", lastPoint: "" };
  }
  const [middleText, firstPoint, lastPoint] = extractTextPolygonAnchorPoints(fullText);
  return { ...row, anchor, middleText, firstPoint, lastPoint };
}

/**
 * Rebuild a multipolygon from edit rows.
 *
 * The pinned endpoints stored on each anchored row are reattached around its
 * middle text. Empty rows are skipped and invalid polygons (fewer than three
 * vertices or a malformed `x,y` pair) are dropped, so partially-edited rows
 * never reach the stored value. Returns `null` when no valid polygon remains.
 */
export function rowsToMultiPolygon(rows) {
  if (!Array.isArray(rows)) return null;
  const polygons = rows
    .map((row) => {
      const middle = (row.middleText || "").trim();
      if (middle === "") return null;
      const fullText =
        row.anchor && row.firstPoint && row.lastPoint ? `${row.firstPoint};${middle};${row.lastPoint}` : middle;
      const points = textPolygonToInternalValue(fullText);
      return isValidPolygon(points) ? points : null;
    })
    .filter(Boolean);
  return polygons.length ? polygons : null;
}
